import { useEffect, useRef } from 'react';
import type { FeedBadges } from '../../domain/leaderboard/badges';
import { hasAnyBadge } from '../../domain/leaderboard/badges';
import { CATEGORY_LABELS } from '../../domain/scoring/categories';
import type { Category } from '../../domain/scoring/types';
import './ResultDisplay.css';

export interface ResultDisplayProps {
  isOpen: boolean;
  onClose: () => void;
  result: {
    exerciseName: string;
    enteredWeight: number;
    reps: number;
    estimated1Rm: number;
    normalized1Rm: number;
    competitionLiftName: string;
    points: number;
    isNewCategoryBest: boolean;
    previousBestPoints?: number;
    monthlyTotalBefore: number;
    monthlyTotalAfter: number;
    category: Category;
    /** The same awards the feed will show for this lift. */
    badges: FeedBadges;
  };
}

export function ResultDisplay({ isOpen, onClose, result }: ResultDisplayProps) {
  const doneBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      if (doneBtnRef.current) {
        doneBtnRef.current.focus();
      }
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const pointsEarned = result.isNewCategoryBest
    ? result.points - (result.previousBestPoints || 0)
    : 0;

  const encouragement = result.isNewCategoryBest ? 'Awesome lifting!' : 'Solid work!';

  return (
    <div
      className="result-display__overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Lift Result"
    >
      <div className="result-display__card">
        <h2 className="result-display__title">{result.exerciseName}</h2>
        <div className="result-display__subtitle">
          {result.enteredWeight} lb × {result.reps}
        </div>

        <div className="result-display__stats">
          <div className="result-display__stat-row">
            <span>Est. 1RM</span>
            <strong>{result.estimated1Rm} lb</strong>
          </div>
          <div className="result-display__stat-row">
            <span>Equiv. {result.competitionLiftName}</span>
            <strong>{result.normalized1Rm} lb</strong>
          </div>
        </div>

        <div
          className={`result-display__points-container ${result.isNewCategoryBest ? 'result-display__points-container--best' : ''}`}
        >
          <div className="result-display__points-value">{result.points}</div>
          <div className="result-display__points-label">PTS</div>
        </div>

        {/* Same wording and colours as the feed, so an award reads the same in
            both places. The old "New category best!" line said this less
            precisely, and is now covered by the category badges. */}
        {hasAnyBadge(result.badges) && (
          <div className="result-display__badges">
            {result.badges.isCategoryPr && (
              <span className="result-display__badge result-display__badge--category-pr">
                <span aria-hidden="true">★</span> {CATEGORY_LABELS[result.category]} PR
              </span>
            )}
            {result.badges.isExercisePr && (
              <span className="result-display__badge result-display__badge--lift-pr">
                <span aria-hidden="true">▲</span> Lift PR
              </span>
            )}
            {result.badges.isCategoryMonthBest && (
              <span className="result-display__badge result-display__badge--month">
                <span aria-hidden="true">●</span> {CATEGORY_LABELS[result.category]} MB
              </span>
            )}
          </div>
        )}

        {result.isNewCategoryBest && pointsEarned > 0 && (
          <div className="result-display__earned">
            +{Math.round(pointsEarned * 10) / 10} points this month
          </div>
        )}

        <div className="result-display__monthly">
          Monthly total: {Math.round(result.monthlyTotalBefore * 10) / 10} →{' '}
          <strong>{Math.round(result.monthlyTotalAfter * 10) / 10}</strong>
        </div>

        <div className="result-display__message">{encouragement}</div>

        <button
          ref={doneBtnRef}
          className="result-display__done"
          onClick={onClose}
          aria-label="Close result"
        >
          DONE
        </button>
      </div>
    </div>
  );
}
